// Textos de los componentes de presentacion y de las estrategias de pasarela.
//
// El diccionario global del widget (src/i18n) es la fuente de verdad y se consulta
// primero: `t()` devuelve la clave tal cual cuando no la conoce, y solo en ese caso se
// usa la tabla local de abajo. Aqui viven unicamente las frases especificas de las
// pasarelas (avisos PCI, escenarios del sandbox, retorno de Webpay) que el diccionario
// compartido no cubre; nada que ya exista alli se duplica.

import type { Locale } from '../i18n';
import { t } from '../i18n';

type Phrases = Record<string, string>;

const ES_CL: Phrases = {
  'method.hint':
    'Los datos de tu medio de pago se ingresan en el formulario de la pasarela; ApiPay nunca los recibe.',
  'method.none': 'El comercio no tiene medios de pago habilitados para esta sesión.',

  'webpay.returning': 'Confirmando el pago con Transbank…',
  // La vuelta SIN PAGO: no se esta confirmando nada, se esta cerrando el cobro. Decir
  // «confirmando tu pago» a quien acaba de pulsar «Anular compra y volver» es contarle lo
  // contrario de lo que pidio.
  'webpay.closing': 'Cerrando el cobro con Transbank…',

  'mercadopago.preparing': 'Preparando el formulario seguro de MercadoPago…',
  'mercadopago.notice':
    'El formulario lo entrega y aloja MercadoPago. ApiPay no recibe ni almacena los datos de tu medio de pago.',

  'sandbox.button': 'Pagar (simulado)',
  'sandbox.notice':
    'Pasarela simulada: no se cobra dinero real y el resultado es determinista según los dos últimos dígitos del monto.',
  'sandbox.scenarios': 'Escenarios deterministas',
  'sandbox.suffix_header': 'Termina en',
  'sandbox.outcome_header': 'Resultado',
  'sandbox.current': 'Tu monto termina en {suffix}: {outcome}',
  'sandbox.outcome.approved': 'Aprobación inmediata',
  'sandbox.outcome.declined': 'Rechazo por fondos insuficientes',
  'sandbox.outcome.timeout': 'Timeout de la pasarela; se resuelve por webhook',
  'sandbox.outcome.requires_action': 'Requiere una acción del pagador',

  'error.redirect_blocked':
    'No pudimos abrir el sitio de la pasarela. Revisa que tu navegador permita la redirección.',

  'santander.notice':
    'El pago se completa en una ventana de Banco Santander. Esta pantalla se actualiza sola cuando termine.',
  'santander.open': 'Pagar en Santander',
  'santander.waiting': 'Completa el pago en la ventana de Santander. Te avisamos aquí en cuanto el banco lo confirme.',
  'santander.reopen': 'Volver a la ventana del banco',
  'santander.blocked':
    'Tu navegador bloqueó la ventana de Santander. Permite las ventanas emergentes para este sitio y vuelve a pulsar «Pagar en Santander».',
  'santander.pending.title': 'Pago pendiente de autorización',
  'santander.pending.body':
    'Tu empresa debe completar las firmas en el banco. El comercio recibirá la confirmación cuando el banco la apruebe.',
};

const EN: Phrases = {
  'method.hint': 'Your payment details are entered in the gateway form; ApiPay never receives them.',
  'method.none': 'The merchant has no payment methods enabled for this session.',

  'webpay.returning': 'Confirming the payment with Transbank…',
  'webpay.closing': 'Closing the payment with Transbank…',

  'mercadopago.preparing': 'Preparing the secure MercadoPago form…',
  'mercadopago.notice':
    'The form is rendered and hosted by MercadoPago. ApiPay never receives or stores your payment details.',

  'sandbox.button': 'Pay (simulated)',
  'sandbox.notice':
    'Simulated gateway: no real money is charged and the outcome is deterministic, driven by the last two digits of the amount.',
  'sandbox.scenarios': 'Deterministic scenarios',
  'sandbox.suffix_header': 'Ends in',
  'sandbox.outcome_header': 'Outcome',
  'sandbox.current': 'Your amount ends in {suffix}: {outcome}',
  'sandbox.outcome.approved': 'Immediate approval',
  'sandbox.outcome.declined': 'Declined for insufficient funds',
  'sandbox.outcome.timeout': 'Gateway timeout; resolved later by webhook',
  'sandbox.outcome.requires_action': 'Requires an action from the buyer',

  'error.redirect_blocked':
    'We could not open the gateway site. Check that your browser allows the redirect.',

  'santander.notice':
    'The payment is completed in a Banco Santander window. This screen updates itself when it is done.',
  'santander.open': 'Pay with Santander',
  'santander.waiting': 'Complete the payment in the Santander window. We will update this screen as soon as the bank confirms it.',
  'santander.reopen': 'Back to the bank window',
  'santander.blocked':
    'Your browser blocked the Santander window. Allow pop-ups for this site and press «Pay with Santander» again.',
  'santander.pending.title': 'Payment pending authorization',
  'santander.pending.body':
    'Your company must complete the signatures at the bank. The merchant will receive the confirmation once the bank approves it.',
};

const FALLBACK_PHRASES: Record<Locale, Phrases> = { 'es-CL': ES_CL, en: EN };

function interpolate(template: string, vars: Record<string, string | number> | undefined): string {
  if (vars === undefined) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match: string, name: string): string => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

function localPhrase(locale: Locale, key: string): string | null {
  return FALLBACK_PHRASES[locale][key] ?? ES_CL[key] ?? null;
}

/**
 * Texto localizado de un componente del widget. Devuelve la clave solo cuando ni el
 * diccionario global ni el local la conocen; quien necesite distinguir ese caso compara
 * el resultado con la clave (ver `gatewayLabel` y `errorCopy`).
 */
export function widgetText(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number> | undefined,
): string {
  const shared = t(locale, key, vars);
  if (shared !== key) {
    return shared;
  }
  const local = localPhrase(locale, key);
  return local === null ? key : interpolate(local, vars);
}

/**
 * Nombre visible de una pasarela. Prioriza el nombre localizado del catalogo
 * (`method.<gateway_id>`), luego el `display_name` que el tenant configuro y por ultimo
 * la etiqueta generica, para que una pasarela nueva en el backend siga siendo legible.
 */
export function gatewayLabel(
  locale: Locale,
  gatewayId: string,
  displayName?: string | undefined,
): string {
  const key = `method.${gatewayId}`;
  const label = widgetText(locale, key);
  if (label !== key) {
    return label;
  }
  if (displayName !== undefined && displayName !== '') {
    return displayName;
  }
  return widgetText(locale, 'method.unknown');
}

/**
 * Copia asociada a un `code` de problem+json. Devuelve null cuando el codigo es
 * desconocido para esta build, para que quien la use decida el respaldo: la UI nunca
 * decide por el texto de `detail`.
 */
export function errorCopy(locale: Locale, code: string): string | null {
  const key = `error.${code}`;
  const text = widgetText(locale, key);
  return text === key ? null : text;
}
